import React from "react";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { fetchCandidates } from "../../data/Candidates";
import { useQuery } from "@tanstack/react-query";
import CandidateListFilter from "./CandidateListFilter";
import { IAddress } from "../../data/Address";
import TableToolbar from "../misc/TableToolbar";
import CandidateListTable from "./CandidateListTable";

export interface ICandidateFilter {
  search: string;
  nationality: number | null;
  location: IAddress | null;
  // sectors: number[];
  experience: number[];
  technical_skills: number[];
  levels: number[];
  specialties: number[];
  languages: number[];
  areas: number[];
  driving_licence: number | null;
  max_notice_period: number;
  status: number | null;
  salary_min: string;
  salary_max: string;
  permanent_resident: boolean;
  qualifies_for_bee: boolean;
  valid_work_permit: boolean;
}

const addressFields = [
  "formatted_address",
  "municipality",
  "municipality_subdivision",
  "country_tertiary_subdivision",
  "country_secondary_subdivision",
  "country_subdivision",
  "country",
];

export default function CandidateList() {
  document.title = "Candidates";

  const { control, handleSubmit, reset } = useForm<ICandidateFilter>({
    defaultValues: {
      search: "",
      nationality: null,
      location: null,
      experience: [],
      technical_skills: [],
      levels: [],
      specialties: [],
      languages: [],
      areas: [],
      driving_licence: null,
      max_notice_period: 12,
      status: null,
      salary_min: "",
      salary_max: "",
      permanent_resident: false,
      qualifies_for_bee: false,
      valid_work_permit: false,
    },
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const location = React.useMemo(() => {
    const fields = Object.fromEntries(
      addressFields.map((field) => [
        field,
        searchParams.get(field) ?? undefined,
      ])
    );
    const isDefined = Object.values(fields).some((val) => val !== undefined);
    return isDefined ? (fields as IAddress) : null;
  }, [searchParams]);

  const filter = React.useMemo(() => {
    const search = searchParams.get("search");
    const nationality = searchParams.get("nationality");
    // const sectors = searchParams.getAll("sectors").map(Number);
    const experience = searchParams.getAll("experience").map(Number);
    const technicalSkills = searchParams.getAll("technical_skills").map(Number);
    const levels = searchParams.getAll("levels").map(Number);
    const specialties = searchParams.getAll("specialties").map(Number);
    const languages = searchParams.getAll("languages").map(Number);
    const areas = searchParams.getAll("areas").map(Number);
    const drivingLicence = searchParams.get("driving_licence");
    const maximumNoticePeriod = Number(
      searchParams.get("max_notice_period") ?? 12
    );
    const status = searchParams.get("status");
    const salaryMin = searchParams.get("salary_min");
    const salaryMax = searchParams.get("salary_max");
    const permanent_resident = searchParams.get("permanent_resident");
    const qualifies_for_bee = searchParams.get("qualifies_for_bee");
    const valid_work_permit = searchParams.get("valid_work_permit");

    return {
      search: search ?? "",
      nationality: nationality ? Number(nationality) : null,
      location,
      experience,
      technical_skills: technicalSkills,
      levels,
      specialties,
      languages,
      areas,
      driving_licence: drivingLicence ? Number(drivingLicence) : null,
      max_notice_period: Number.isNaN(maximumNoticePeriod)
        ? 12
        : maximumNoticePeriod,
      status: status ? Number(status) : null,
      salary_min: salaryMin ?? "",
      salary_max: salaryMax ?? "",
      permanent_resident: permanent_resident === "true",
      qualifies_for_bee: qualifies_for_bee === "true",
      valid_work_permit: valid_work_permit === "true",
    } as ICandidateFilter;
  }, [searchParams, location]);

  React.useEffect(() => {
    reset(filter);
  }, [filter, reset]);

  const {
    data: candidates,
    error,
    isLoading: candidatesLoading,
  } = useQuery({
    queryKey: ["candidates", searchParams.toString()],
    queryFn: () => fetchCandidates(searchParams.toString()),
  });

  // const [candidates, candidatesLoading] = useCandidates(
  //   searchParams.toString()
  // );

  const handleFilter = (data: ICandidateFilter) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("search");
      if (data.search) params.set("search", data.search);
      params.delete("nationality");
      if (data.nationality)
        params.set("nationality", data.nationality.toString());
      addressFields.forEach((field) => {
        params.delete(field);
      });
      if (data.location) {
        const location = data.location as Record<string, string | undefined>;
        addressFields.forEach((field) => {
          const value = location[field];
          if (value) {
            params.set(field, value);
          }
        });
      }

      params.delete("experience");
      data.experience.forEach((experience) => {
        params.append("experience", experience.toString());
      });
      params.delete("technical_skills");
      data.technical_skills.forEach((skill) => {
        params.append("technical_skills", skill.toString());
      });
      params.delete("levels");
      data.levels.forEach((level) => {
        params.append("levels", level.toString());
      });
      params.delete("specialties");
      data.specialties.forEach((specialty) => {
        params.append("specialties", specialty.toString());
      });
      params.delete("languages");
      data.languages.forEach((language) => {
        params.append("languages", language.toString());
      });
      params.delete("areas");
      data.areas.forEach((area) => {
        params.append("areas", area.toString());
      });
      params.delete("driving_licence");
      if (data.driving_licence)
        params.set("driving_licence", data.driving_licence.toString());
      params.delete("max_notice_period");
      if (data.max_notice_period) {
        params.set("max_notice_period", data.max_notice_period.toString());
      }
      params.delete("status");
      if (data.status) params.set("status", data.status.toString());
      params.delete("salary_min");
      if (data.salary_min) params.set("salary_min", data.salary_min);
      params.delete("salary_max");
      if (data.salary_max) params.set("salary_max", data.salary_max);
      params.delete("permanent_resident");
      if (data.permanent_resident) params.set("permanent_resident", "true");
      params.delete("qualifies_for_bee");
      if (data.qualifies_for_bee) params.set("qualifies_for_bee", "true");
      params.delete("valid_work_permit");
      if (data.valid_work_permit) params.set("valid_work_permit", "true");

      params.delete("page");
      return params;
    });
  };

  return (
    <Box
      sx={{
        display: "grid",
        borderBottom: 1,
        borderColor: "grey.300",
        gridTemplateColumns: `1fr 320px`,
        gridTemplateAreas: `
      "table filter"
      `,
      }}
    >
      <Box sx={{ gridArea: "table" }}>
        <TableToolbar name="Candidates" create="/candidates/create" />
        {candidatesLoading ? <LinearProgress sx={{ mt: -0.5 }} /> : null}
        <CandidateListTable
          candidates={candidates}
          isLoading={candidatesLoading}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          filter={filter}
        />
      </Box>
      <Box
        component="form"
        onSubmit={handleSubmit(handleFilter)}
        autoComplete="off"
        sx={{ gridArea: "filter", borderLeft: 1, borderColor: "grey.300" }}
      >
        <CandidateListFilter control={control} />
      </Box>
    </Box>
  );
}
